import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiException, OrgUnitModel, User } from '../../../../types';
import {
  UserKeys,
  disableTwoFactor,
  forceSignout,
  getScheduledUserStatusUpdate,
  getUserDecomStatus,
  getVerificationCodes,
  updateUser,
} from '../../../api/users-api';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import password from 'secure-random-password';
import { handleErrors } from '../../../utils/errors';
import axios from 'axios';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import { pagesConfig } from '../../../shared/pages-config';
import ScheduledUserUpdateDialog from './scheduled/scheduled-user-status-update-dialog';
import ScheduledUserStatusCancelDialog from './scheduled/scheduled-user-status-cancel-dialog';
import { useUserSettings } from '../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import { tiers } from '../../../shared/gPanel-tiers';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopy from '@mui/icons-material/ContentCopy';
import SharedButton from '../../../shared/buttons/shared-button';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';
import Shuffle from '@mui/icons-material/Shuffle';

interface GeneralSettingsFormProps {
  user: User;
  userRoles: string[];
  gPanelRoles: string[];
  gPanelUserRole: string;
  domains: string[];
  orgUnits: OrgUnitModel[];
  changeLogRoles: string[];
  canSeePasswords: boolean;
}

interface UserFormData extends User {
  gPanelRole?: string;
  cPanelRole?: string;
  status?: string;
  userName?: string;
  domain?: string;
}

export default function GeneralSettingsForm({
  user,
  userRoles,
  gPanelRoles,
  gPanelUserRole,
  domains,
  orgUnits,
  changeLogRoles,
  canSeePasswords,
}: GeneralSettingsFormProps) {
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const navigate = useNavigate();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [signoutButtonClicked, setSignoutButtonClicked] = useState(false);
  const [signoutButtonLoading, setSignoutButtonLoading] = useState(false);
  const [showBackupCodesClicked, setShowBackupCodesClicked] = useState(false);
  const [showBackupCodesLoading, setShowBackupCodesLoading] = useState(false);
  const [openScheduledUserUpdateDialog, setOpenScheduledUserUpdateDialog] =
    useState(false);
  const [
    openScheduledUserUpdateCancelDialog,
    setOpenScheduledUserUpdateCancelDialog,
  ] = useState(false);
  const [disabledTwoFactorClicked, setDisabledTwoFactorClicked] =
    useState(false);
  const [disabledTwoFactorLoading, setDisabledTwoFactorLoading] =
    useState(false);
  const queryClient = useQueryClient();
  const userStatus = user.isArchived
    ? 'archived'
    : user.suspended
    ? 'suspended'
    : 'active';
  const { primaryDomain, usersTier, features } = useUserSettings();

  const {
    data: userDecomStatus,
    isLoading: isUserDecomStatusLoading,
    isError: isUserDecomStatusError,
  } = useQuery({
    queryKey: [UserKeys.DecomStatus, user.primaryEmail],
    queryFn: () => getUserDecomStatus(user.primaryEmail),
    staleTime: 0,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('user decommission status'),
    },
  });

  const {
    data: scheduledUserStatusUpdate,
    isLoading: isGetScheduledUserStatusUpdateLoading,
    isError: getScheduledUserStatusUpdateError,
  } = useQuery({
    queryKey: [UserKeys.ScheduledUserStatusUpdate, user.primaryEmail],
    queryFn: () => getScheduledUserStatusUpdate(user.primaryEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading(
        'scheduled user status update'
      ),
    },
  });

  const standardTier = tiers.find((t) => t.value === 'standard');
  const userDoesntHaveTier = standardTier
    ? usersTier.rank < standardTier.rank
    : false;

  const isUserInDecommPolicy =
    !isUserDecomStatusLoading && isUserDecomStatusError
      ? false
      : userDecomStatus
      ? userDecomStatus.isactive || !userDecomStatus.isTerminated
      : false;

  const tooltipMessage = userDoesntHaveTier
    ? Messages.Errors.Tier.Acting(standardTier!.displayValue)
    : isUserInDecommPolicy
    ? 'Cannot schedule a status change for a user that is in a decommissioning policy.'
    : '';

  function mapStatus(status: string) {
    switch (status) {
      case 'active':
        return 'Active';
      case 'suspended':
        return 'Suspend';
      case 'archived':
        return 'Archive';
      default:
        return 'Unknown';
    }
  }

  const { mutate, isPending: updatingUser } = useMutation({
    mutationFn: (data: User) =>
      updateUser(
        user,
        data,
        gPanelUserRole,
        getValues().gPanelRole,
        changeLogMessage
      ),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('User'),
      });

      queryClient.invalidateQueries({ queryKey: [UserKeys.UserList] });

      queryClient.setQueryData(
        [UserKeys.gPanelRole, data.primaryEmail],
        getValues().gPanelRole
      );
      queryClient.setQueryData([UserKeys.User, data.primaryEmail], data);
      setChangeLogMessage('');
      reset();

      if (data.primaryEmail !== user.primaryEmail) {
        navigate(`/${pagesConfig.users.link}/${data.primaryEmail}`, {
          replace: true,
        });
      }
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;

        if (errorResponse.exceptionType === 'ReusedPassword') {
          setFormError('password', {
            type: 'manual',
            message: 'Password cannot be the same as the old password.',
          });
        } else if (errorResponse.message?.includes('Entity already exists')) {
          setFormError('userName', {
            type: 'manual',
            message: 'User Name already exists.',
          });
        } else {
          handleErrors({
            error: error,
            snackbarMessage: Messages.Errors.Network.Updating('user'),
          });
        }
      } else {
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Updating('user'),
        });
      }
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.general_settings
          .update_general_settings,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.generalSettings,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isLoading, isValidating, errors, dirtyFields },
    setValue,
    getValues,
    setError: setFormError,
  } = useForm({
    values: {
      ...user,
      password: user.password || '',
      gPanelRole: gPanelUserRole || '',
      userName: user.primaryEmail.split('@')[0],
      domain: user.primaryEmail.split('@')[1],
      cPanelRole: user.isAdmin ? 'admin' : 'user',
      status: user.isArchived
        ? 'archived'
        : user.suspended
        ? 'suspended'
        : 'active',
    },
    reValidateMode: 'onBlur',
  });

  function mutateData() {
    const dbUser = {
      ...getValues(),
      password: getValues('password')?.length
        ? getValues('password')
        : user.password,
      isAdmin: getValues('cPanelRole') === 'admin',
      primaryEmail: `${getValues('userName')}@${getValues('domain')}`,
      isArchived: getValues('status') === 'archived',
      suspended: getValues('status') === 'suspended',
    } as UserFormData;

    // Remove unneeded fields
    delete dbUser.userName;
    delete dbUser.cPanelRole;
    delete dbUser.gPanelRole;
    delete dbUser.domain;
    delete dbUser.status;

    return dbUser;
  }

  const onSubmit: SubmitHandler<UserFormData> = () => {
    if (
      (dirtyFields.status &&
        getValues('status') === 'active' &&
        isAbleToSeeUIElement(changeLogRoles, UserRoles.ActivateUsers.value)) ||
      (dirtyFields.status &&
        getValues('status') === 'suspended' &&
        isAbleToSeeUIElement(changeLogRoles, UserRoles.SuspendUsers.value)) ||
      (dirtyFields.status &&
        getValues('status') === 'archived' &&
        isAbleToSeeUIElement(changeLogRoles, UserRoles.ArchiveUsers.value)) ||
      (dirtyFields.orgUnitPath &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.EditUserOrgUnit.value
        )) ||
      (dirtyFields.domain &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.ChangeEmailAddress.value
        )) ||
      (dirtyFields.userName &&
        isAbleToSeeUIElement(changeLogRoles, UserRoles.EditUserName.value)) ||
      (dirtyFields.password &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.SetCustomPasswords.value
        )) ||
      (dirtyFields.password &&
        isAbleToSeeUIElement(changeLogRoles, UserRoles.SetPasswords.value)) ||
      (dirtyFields.changePasswordAtNextLogin &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.ForcePasswordReset.value
        )) ||
      (dirtyFields.includeInGlobalAddressList &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.ShareContactWithDirectory.value
        )) ||
      (dirtyFields.cPanelRole &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.ChangeCPanelRole.value
        )) ||
      (dirtyFields.gPanelRole &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.ChangeGPanelRole.value
        )) ||
      (dirtyFields.recoveryEmail &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.EditRecoveryEmail.value
        )) ||
      (dirtyFields.recoveryPhone &&
        isAbleToSeeUIElement(
          changeLogRoles,
          UserRoles.EditRecoveryPhone.value
        )) ||
      (user.isAdmin !== (getValues('cPanelRole') === 'admin') &&
        isAbleToSeeUIElement(changeLogRoles, UserRoles.EditAdmins.value))
    ) {
      setOpenChangeLogDialog(true);
    } else {
      const dbUser = mutateData();
      mutate(dbUser);
    }
  };

  return (
    <Box
      component="form"
      sx={{ position: 'relative', height: '100%' }}
      onSubmit={handleSubmit(onSubmit)}
    >
      {openScheduledUserUpdateDialog ? (
        <ScheduledUserUpdateDialog
          open={openScheduledUserUpdateDialog}
          onClose={() => setOpenScheduledUserUpdateDialog(false)}
          userStatus={userStatus}
          username={user.primaryEmail.split('@')[0]}
          userRoles={userRoles}
          user={user}
          primaryDomain={primaryDomain}
          changeLogRoles={changeLogRoles}
        />
      ) : null}

      {openScheduledUserUpdateCancelDialog ? (
        <ScheduledUserStatusCancelDialog
          open={openScheduledUserUpdateCancelDialog}
          onClose={() => setOpenScheduledUserUpdateCancelDialog(false)}
          userEmail={user.primaryEmail}
          changeLogRoles={changeLogRoles}
        />
      ) : null}

      <Stack
        flexDirection="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        sx={{ position: 'absolute', top: -60, right: 0 }}
      >
        <SharedIconButton
          variant="refresh"
          tooltipLabel="Refresh"
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: [UserKeys.User] });
            queryClient.invalidateQueries({
              queryKey: [UserKeys.gPanelRoles],
            });
            queryClient.invalidateQueries({
              queryKey: [UserKeys.UserRole],
            });
            queryClient.invalidateQueries({
              queryKey: [UserKeys.Domains],
            });
            queryClient.invalidateQueries({
              queryKey: [UserKeys.OrgUnitsFromGoogle],
            });
            queryClient.invalidateQueries({
              queryKey: [UserKeys.ScheduledUserStatusUpdate, user.primaryEmail],
            });
            queryClient.invalidateQueries({
              queryKey: [[UserKeys.DecomStatus, user.primaryEmail]],
            });
          }}
        />
        <SharedButton
          variant="primary"
          disabled={!isDirty || updatingUser}
          loading={updatingUser || isLoading}
          type="submit"
        >
          Save
        </SharedButton>
      </Stack>

      <Box sx={{ flex: 1, height: '100%', overflowY: 'auto' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Basic Information
        </Typography>
        <Box sx={{ py: 2, px: 4, width: '100%' }}>
          <Stack
            direction="row"
            flexWrap="wrap"
            sx={{ pb: 2 }}
            justifyContent="space-around"
          >
            <Stack direction="column" spacing={2} pb={4}>
              <Stack direction="row" spacing={2}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      inputProps={{
                        id: 'status',
                      }}
                      InputLabelProps={{
                        htmlFor: 'status',
                      }}
                      select
                      label="Status"
                      sx={{ minWidth: 150 }}
                      value={field.value || ''}
                      onChange={field.onChange}
                    >
                      <MenuItem
                        value={'active'}
                        disabled={
                          !isAbleToSeeUIElement(
                            userRoles,
                            UserRoles.ActivateUsers.value
                          )
                        }
                      >
                        Active
                      </MenuItem>
                      <MenuItem
                        value={'suspended'}
                        disabled={
                          !isAbleToSeeUIElement(
                            userRoles,
                            UserRoles.SuspendUsers.value
                          )
                        }
                      >
                        Suspended
                      </MenuItem>
                      <MenuItem
                        value={'archived'}
                        disabled={
                          !isAbleToSeeUIElement(
                            userRoles,
                            UserRoles.ArchiveUsers.value
                          )
                        }
                      >
                        Archived
                      </MenuItem>
                    </TextField>
                  )}
                />
                <Tooltip
                  title={tooltipMessage}
                  arrow
                  disableHoverListener={
                    !isUserInDecommPolicy && !userDoesntHaveTier
                  }
                >
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => setOpenScheduledUserUpdateDialog(true)}
                      loading={
                        isUserDecomStatusLoading ||
                        isGetScheduledUserStatusUpdateLoading
                      }
                      disabled={
                        userDoesntHaveTier ||
                        isUserInDecommPolicy ||
                        isUserDecomStatusLoading ||
                        isGetScheduledUserStatusUpdateLoading ||
                        isUserDecomStatusError ||
                        getScheduledUserStatusUpdateError ||
                        !!scheduledUserStatusUpdate || // user already has a scheduled update
                        !isAbleToSeeUIElement(
                          userRoles,
                          UserRoles.ScheduleUserStatusUpdate.value
                        )
                      }
                    >
                      SCHEDULE STATUS CHANGE
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
              {scheduledUserStatusUpdate &&
                scheduledUserStatusUpdate.newStatus &&
                scheduledUserStatusUpdate.scheduledDatetime &&
                !getScheduledUserStatusUpdateError && (
                  <Alert
                    severity="info"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        sx={{ height: '80%', fontWeight: 400 }}
                        disabled={
                          !isAbleToSeeUIElement(
                            userRoles,
                            UserRoles.ScheduleUserStatusUpdate.value
                          )
                        }
                        onClick={() =>
                          setOpenScheduledUserUpdateCancelDialog(true)
                        }
                      >
                        Cancel
                      </Button>
                    }
                  >
                    {mapStatus(scheduledUserStatusUpdate.newStatus)} set for{' '}
                    {new Date(
                      scheduledUserStatusUpdate.scheduledDatetime
                    ).toLocaleString()}
                  </Alert>
                )}
              {getScheduledUserStatusUpdateError &&
                !scheduledUserStatusUpdate && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to load scheduled status update. Please Refresh.
                  </Alert>
                )}
            </Stack>

            <Controller
              name="orgUnitPath"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  id="org-unit-autocomplete"
                  value={orgUnits.find((orgUnit) => {
                    if (!field.value) {
                      return orgUnit.orgUnitPath === '/';
                    } else {
                      return orgUnit.orgUnitPath === field.value;
                    }
                  })}
                  onChange={(_event, newValue) => {
                    setValue('orgUnitPath', newValue?.orgUnitPath || '/', {
                      shouldDirty: true,
                    });
                  }}
                  options={orgUnits.sort((a, b) => {
                    if (a.orgUnitPath < b.orgUnitPath) return -1;
                    if (a.orgUnitPath > b.orgUnitPath) return 1;
                    return 0;
                  })}
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.EditUserOrgUnit.value
                    )
                  }
                  getOptionLabel={(option) => option.orgUnitPath}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Organization Unit"
                      sx={{ minWidth: 400 }}
                    />
                  )}
                />
              )}
            />
          </Stack>

          <Stack
            direction="row"
            flexWrap="wrap"
            //spacing={5}
            sx={{ pb: 2 }}
            justifyContent="space-around"
          >
            <Controller
              name="userName"
              control={control}
              rules={{
                required: 'User Name is required.',
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="User Name"
                  autoComplete="off"
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.ChangeEmailAddress.value
                    )
                  }
                  InputProps={{
                    endAdornment: isValidating ? (
                      <CircularProgress size={20} />
                    ) : null,
                  }}
                  error={!!errors.userName}
                  helperText={errors?.userName?.message || ' '}
                  sx={{ minWidth: 400 }}
                />
              )}
            />

            <Controller
              name="domain"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'domain',
                  }}
                  InputLabelProps={{
                    htmlFor: 'domain',
                  }}
                  select
                  label="Domain"
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.ChangeEmailAddress.value
                    )
                  }
                  sx={{ minWidth: 400 }}
                >
                  {domains.map((domain) => (
                    <MenuItem key={domain} value={domain}>
                      {domain}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>

          <Stack
            direction="row"
            flexWrap="wrap"
            //spacing={5}
            //sx={{ mb: 2 }}
            justifyContent="space-around"
          >
            <Controller
              name="name.givenName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name"
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.EditUserName.value
                    )
                  }
                  sx={{ minWidth: 400, pb: 2 }}
                />
              )}
            />

            <Controller
              name="name.familyName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name"
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.EditUserName.value
                    )
                  }
                  sx={{ minWidth: 400 }}
                />
              )}
            />
          </Stack>
        </Box>
        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Password
        </Typography>

        <Stack
          flexDirection="row"
          justifyContent="space-around"
          alignItems="baseline"
          spacing={1}
          sx={{ py: 2, px: 4, width: '100%' }}
        >
          <Stack flexDirection="row" alignItems="baseline" spacing={1}>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="New Password"
                  onCopy={(event) => {
                    if (!canSeePasswords) event.preventDefault();
                  }}
                  onCut={(event) => {
                    if (!canSeePasswords) event.preventDefault();
                  }}
                  type={!canSeePasswords ? 'password' : undefined}
                  error={!!errors.password}
                  helperText={errors?.password?.message || ' '}
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.SetCustomPasswords.value
                    )
                  }
                  sx={{ minWidth: 400 }}
                />
              )}
            />

            <Tooltip title="Create Password" arrow>
              <span>
                <IconButton
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.SetPasswords.value
                    )
                  }
                  onClick={async () => {
                    const generatedPassword = password.randomPassword({
                      length: 16,
                      characters: [
                        password.lower,
                        password.upper,
                        password.digits,
                        password.symbols,
                      ],
                    });

                    setValue('password', generatedPassword, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });

                    if (canSeePasswords) {
                      await navigator.clipboard.writeText(generatedPassword);
                      setSnackbar({
                        open: true,
                        severity: 'info',
                        message: 'Password has been copied to clipboard.',
                      });
                    }
                  }}
                >
                  <Shuffle />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <Controller
            name="changePasswordAtNextLogin"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label="Require user to change password at next sign-in."
                disabled={
                  !isAbleToSeeUIElement(
                    userRoles,
                    UserRoles.ForcePasswordReset.value
                  )
                }
              />
            )}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Contact Sharing
        </Typography>

        <Stack
          flexWrap="wrap"
          sx={{
            py: 2,
            px: 4,
            width: '100%',
            flexDirection: 'row',
            justifyContent: 'space-around',
          }}
        >
          <Controller
            name="includeInGlobalAddressList"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label={`Automatically share ${user?.name?.givenName}'s contact information.`}
                disabled={
                  !isAbleToSeeUIElement(
                    userRoles,
                    UserRoles.ShareContactWithDirectory.value
                  )
                }
              />
            )}
          />
          <Box sx={{ minWidth: '400px' }} />
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Roles
        </Typography>

        <Stack
          flexWrap="wrap"
          spacing={4}
          sx={{
            py: 2,
            px: 4,
            width: '100%',
            justifyContent: 'space-around',
            flexDirection: 'row',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Controller
              name="cPanelRole"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'cpanel-role',
                  }}
                  InputLabelProps={{
                    htmlFor: 'cpanel-role',
                  }}
                  select
                  label="cPanel Role"
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.ChangeCPanelRole.value
                    )
                  }
                  sx={{ minWidth: 400, pb: 2 }}
                >
                  <MenuItem value={'admin'}>Admin</MenuItem>
                  <MenuItem value={'user'}>User</MenuItem>
                </TextField>
              )}
            />
            <Controller
              name="gPanelRole"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'gpanel-role',
                  }}
                  InputLabelProps={{
                    htmlFor: 'gpanel-role',
                  }}
                  select
                  label={
                    isFeatureEnabled('groupsGovernanceBackend', features)
                      ? 'gPanel User Role'
                      : 'gPanel Role'
                  }
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.ChangeGPanelRole.value
                    )
                  }
                  sx={{ minWidth: 400 }}
                >
                  {gPanelRoles?.length ? (
                    gPanelRoles?.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={''}>No Roles Found</MenuItem>
                  )}
                </TextField>
              )}
            />
          </Box>
          <Box sx={{ minWidth: 400 }} />
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          2-Step Verification
        </Typography>
        <Stack
          spacing={4}
          flexDirection="row"
          justifyContent="space-around"
          sx={{ py: 2, px: 4, width: '100%' }}
          flexWrap="wrap"
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Stack flexDirection="row" gap={4} sx={{ pb: 2 }}>
              <Button
                variant="contained"
                loading={disabledTwoFactorLoading}
                disabled={
                  !getValues('isEnrolledIn2Sv') ||
                  user.suspended ||
                  !isAbleToSeeUIElement(
                    userRoles,
                    UserRoles.DisableTwoFactorAuthentication.value
                  )
                }
                onClick={async () => {
                  if (
                    isAbleToSeeUIElement(
                      changeLogRoles,
                      UserRoles.DisableTwoFactorAuthentication.value
                    )
                  ) {
                    setDisabledTwoFactorClicked(true);
                    setOpenChangeLogDialog(true);
                  } else {
                    setDisabledTwoFactorLoading(true);

                    try {
                      await disableTwoFactor(user.primaryEmail);

                      setValue('isEnrolledIn2Sv', false);

                      setSnackbar({
                        open: true,
                        severity: 'success',
                        message: '2-Factor disabled successfully!',
                      });
                    } catch (error) {
                      handleErrors({
                        error: error,
                        snackbarMessage:
                          'There was an issue disabling 2-Factor. Please try again.',
                      });
                    } finally {
                      createEvent(
                        analytics.user_management.user_details.general_settings
                          .disable_2_factor,
                        {
                          module: ModuleList.modules.userManagement.name,
                          section:
                            ModuleList.modules.userManagement.sections
                              .generalSettings,
                        }
                      );
                      setDisabledTwoFactorLoading(false);
                    }
                  }
                }}
              >
                Disable 2-Factor
              </Button>
              <Button
                variant="contained"
                disabled={
                  !isAbleToSeeUIElement(userRoles, UserRoles.SignOutUsers.value)
                }
                loading={signoutButtonLoading}
                onClick={async () => {
                  if (
                    isAbleToSeeUIElement(
                      changeLogRoles,
                      UserRoles.SignOutUsers.value
                    )
                  ) {
                    setSignoutButtonClicked(true);
                    setOpenChangeLogDialog(true);
                  } else {
                    try {
                      setSignoutButtonLoading(true);
                      await forceSignout(user.primaryEmail);
                      setSnackbar({
                        open: true,
                        severity: 'success',
                        message: 'User signed out successfully!',
                      });
                    } catch (error) {
                      handleErrors({
                        error: error,
                        snackbarMessage:
                          'There was an issue signing out user. Please try again.',
                      });
                    } finally {
                      createEvent(
                        analytics.user_management.user_details.general_settings
                          .force_sign_out,
                        {
                          module: ModuleList.modules.userManagement.name,
                          section:
                            ModuleList.modules.userManagement.sections
                              .generalSettings,
                        }
                      );
                      setSignoutButtonLoading(false);
                    }
                  }
                }}
              >
                Force Signout
              </Button>
            </Stack>
            <Stack
              flexDirection="row"
              alignItems="center"
              gap={1}
              sx={{ pb: 2 }}
            >
              <Typography>Verification Enforced:</Typography>
              {getValues('isEnforcedIn2Sv') ? (
                <Chip
                  label="Enabled"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ) : (
                <Chip
                  label="Not Enabled"
                  disabled
                  variant="outlined"
                  size="small"
                />
              )}
            </Stack>

            <Stack
              flexDirection="row"
              alignItems="center"
              gap={1}
              sx={{ pb: 2 }}
            >
              <Typography>Verification Enrolled:</Typography>
              {getValues('isEnrolledIn2Sv') ? (
                <Chip
                  label="Enabled"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ) : (
                <Chip
                  label="Not Enabled"
                  disabled
                  variant="outlined"
                  size="small"
                />
              )}
            </Stack>
            <Button
              variant="contained"
              startIcon={<ContentCopy />}
              loading={showBackupCodesLoading}
              disabled={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.GenerateBackupCodes.value
                ) ||
                user.suspended ||
                !getValues('isEnrolledIn2Sv')
              }
              onClick={async () => {
                if (
                  isAbleToSeeUIElement(
                    changeLogRoles,
                    UserRoles.GenerateBackupCodes.value
                  )
                ) {
                  setShowBackupCodesClicked(true);
                  setOpenChangeLogDialog(true);
                } else {
                  try {
                    setShowBackupCodesLoading(true);
                    const codes = await getVerificationCodes(user.primaryEmail);
                    await navigator.clipboard.writeText(codes.join(', '));
                    setSnackbar({
                      open: true,
                      severity: 'info',
                      message: 'Backup codes have been copied to clipboard.',
                    });
                  } catch (e) {
                    handleErrors({
                      error: e,
                      snackbarMessage:
                        'There was an issue generating backup codes. Please try again.',
                    });
                  } finally {
                    createEvent(
                      analytics.user_management.user_details.general_settings
                        .generate_backup_codes,
                      {
                        module: ModuleList.modules.userManagement.name,
                        section:
                          ModuleList.modules.userManagement.sections
                            .generalSettings,
                      }
                    );
                    setShowBackupCodesLoading(false);
                  }
                }
              }}
            >
              Backup Codes
            </Button>
          </Box>
          <Box sx={{ minWidth: 400 }} />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, mt: 2, mb: 1 }}>
          Recovery Information
        </Typography>
        <Stack
          sx={{
            px: 4,
          }}
          spacing={2}
        >
          <Typography variant="body2">
            Recovery information is used to secure user accounts at sign-in and
            during account recovery.
          </Typography>
          <Stack flexDirection="row" gap={4} flexWrap="wrap">
            <Controller
              name="recoveryEmail"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Recovery Email"
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.EditRecoveryEmail.value
                    )
                  }
                  sx={{ minWidth: 400 }}
                />
              )}
            />
            <Controller
              name="recoveryPhone"
              control={control}
              rules={{
                validate: (value) => {
                  if (value && value !== '') {
                    return (
                      matchIsValidTel(value) ||
                      'Recovery phone must be a valid phone number.'
                    );
                  } else {
                    return true;
                  }
                },
              }}
              render={({ field }) => (
                <MuiTelInput
                  {...field}
                  value={field.value || ''}
                  onChange={(_value, info) => {
                    field.onChange(info.numberValue || '');
                  }}
                  defaultCountry="US"
                  forceCallingCode
                  label="Recovery Phone"
                  sx={{ minWidth: 400 }}
                  disabled={
                    !isAbleToSeeUIElement(
                      userRoles,
                      UserRoles.EditRecoveryPhone.value
                    )
                  }
                  error={!!errors.recoveryPhone}
                  helperText={errors?.recoveryPhone?.message || ' '}
                />
              )}
            />
          </Stack>
        </Stack>
      </Box>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={async () => {
          if (signoutButtonClicked) {
            try {
              setSignoutButtonLoading(true);
              await forceSignout(user.primaryEmail);
              setSnackbar({
                open: true,
                severity: 'success',
                message: 'User signed out successfully!',
              });
            } catch (error) {
              handleErrors({
                error: error,
                snackbarMessage:
                  'There was an issue signing out user. Please try again.',
              });
            } finally {
              createEvent(
                analytics.user_management.user_details.general_settings
                  .force_sign_out,
                {
                  module: ModuleList.modules.userManagement.name,
                  section:
                    ModuleList.modules.userManagement.sections.generalSettings,
                }
              );
              setSignoutButtonClicked(false);
              setSignoutButtonLoading(false);
              setChangeLogMessage('');
              setOpenChangeLogDialog(false);
            }
          } else if (disabledTwoFactorClicked) {
            setDisabledTwoFactorLoading(true);
            try {
              await disableTwoFactor(user.primaryEmail);
              setValue('isEnrolledIn2Sv', false);
              setSnackbar({
                open: true,
                severity: 'success',
                message: '2-Factor disabled successfully!',
              });
            } catch (error) {
              handleErrors({
                error: error,
                snackbarMessage:
                  'There was an issue disabling 2-Factor. Please try again.',
              });
            } finally {
              createEvent(
                analytics.user_management.user_details.general_settings
                  .disable_2_factor,
                {
                  module: ModuleList.modules.userManagement.name,
                  section:
                    ModuleList.modules.userManagement.sections.generalSettings,
                }
              );
              setDisabledTwoFactorClicked(false);
              setDisabledTwoFactorLoading(false);
              setChangeLogMessage('');
              setOpenChangeLogDialog(false);
            }
          } else if (showBackupCodesClicked) {
            try {
              setShowBackupCodesLoading(true);
              const codes = await getVerificationCodes(user.primaryEmail);
              await navigator.clipboard.writeText(codes.join(', '));
              setSnackbar({
                open: true,
                severity: 'info',
                message: 'Backup codes have been copied to clipboard.',
              });
            } catch (e) {
              handleErrors({
                error: e,
                snackbarMessage:
                  'There was an issue generating backup codes. Please try again.',
              });
            } finally {
              createEvent(
                analytics.user_management.user_details.general_settings
                  .generate_backup_codes,
                {
                  module: ModuleList.modules.userManagement.name,
                  section:
                    ModuleList.modules.userManagement.sections.generalSettings,
                }
              );
              setShowBackupCodesClicked(false);
              setShowBackupCodesLoading(false);
              setChangeLogMessage('');
              setOpenChangeLogDialog(false);
            }
          } else {
            const dbUser = mutateData();
            mutate(dbUser);
          }
        }}
        mutateLoading={
          updatingUser ||
          signoutButtonLoading ||
          disabledTwoFactorLoading ||
          showBackupCodesLoading
        }
      />
    </Box>
  );
}
